// =============================================================================
// Activity client — the browser half of user activity logging
// (docs/designs/USER_ACTIVITY_LOGS_AND_ANALYTICS.md §1, "web").
//
// Three pieces, each small:
//   getOrCreateSessionId  a session id in localStorage that rolls over after 30
//                         minutes of inactivity (shared across tabs).
//   createActivityQueue   batches events and posts them to /api/activity; a
//                         pagehide flush goes through navigator.sendBeacon.
//   installFetchObserver  wraps window.fetch ONCE and reports every same-origin
//                         mutating /api call. It reads response.status and
//                         nothing else — the body is never touched, so streams
//                         and uploads behave exactly as before.
//
// Nothing here may ever throw into application code: logging is a side effect,
// and a broken side effect must not break the product.
// =============================================================================

import { deriveApiAction, shouldTrackApiCall } from './actions';
import type { ActivityEventInput } from './types';

export type { ActivityEventInput } from './types';

export const SESSION_STORAGE_KEY = 'genplay_activity_session';
export const SESSION_IDLE_MS = 30 * 60 * 1000;
const ACTIVITY_ENDPOINT = '/api/activity';
const DEFAULT_FLUSH_DELAY_MS = 1500;
const DEFAULT_MAX_BATCH = 20;

// ---------------------------------------------------------------------------
// Session id
// ---------------------------------------------------------------------------

interface StoredSession {
  id: string;
  last: number;
}

/** In-memory fallback when storage is unavailable, so one tab still has one session. */
let memorySession: StoredSession | null = null;

function randomId(): string {
  const cryptoApi = typeof globalThis.crypto !== 'undefined' ? globalThis.crypto : undefined;
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') return cryptoApi.randomUUID();
  const time = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 12);
  return `${time}-${rand}`;
}

function readStoredSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as StoredSession).id === 'string' &&
      typeof (parsed as StoredSession).last === 'number'
    ) {
      return parsed as StoredSession;
    }
    return null;
  } catch {
    return null;
  }
}

function writeStoredSession(session: StoredSession): void {
  try {
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Storage blocked: the memory copy carries the session for this tab.
  }
}

/**
 * The current session id. Every call counts as activity and extends the
 * session; a gap longer than SESSION_IDLE_MS starts a new one.
 */
export function getOrCreateSessionId(now: number = Date.now()): string {
  const stored = readStoredSession() ?? memorySession;
  const session =
    stored && now - stored.last <= SESSION_IDLE_MS
      ? { id: stored.id, last: now }
      : { id: randomId(), last: now };
  memorySession = session;
  writeStoredSession(session);
  return session.id;
}

// ---------------------------------------------------------------------------
// Queue
// ---------------------------------------------------------------------------

export type FlushReason = 'timer' | 'size' | 'manual' | 'pagehide';

export interface SendOptions {
  /** True when the page is going away: use sendBeacon, which survives unload. */
  beacon: boolean;
}

export type SendFn = (body: string, options: SendOptions) => void | Promise<void>;

export interface ActivityQueueOptions {
  send?: SendFn;
  flushDelayMs?: number;
  maxBatch?: number;
  endpoint?: string;
}

export interface ActivityQueue {
  enqueue(event: ActivityEventInput): void;
  flush(reason?: FlushReason): void;
  setEnabled(enabled: boolean): void;
  size(): number;
  dispose(): void;
}

/**
 * The default transport. `originalFetch` is captured before the observer
 * patches window.fetch, so posting the log can never be observed as an action.
 */
function defaultSend(endpoint: string, originalFetch: typeof fetch | null): SendFn {
  return (body, { beacon }) => {
    if (beacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(endpoint, blob)) return;
    }
    if (!originalFetch) return;
    return originalFetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'same-origin'
    }).then(
      () => undefined,
      () => undefined
    );
  };
}

export function createActivityQueue(options: ActivityQueueOptions = {}): ActivityQueue {
  const flushDelayMs = options.flushDelayMs ?? DEFAULT_FLUSH_DELAY_MS;
  const maxBatch = options.maxBatch ?? DEFAULT_MAX_BATCH;
  const endpoint = options.endpoint ?? ACTIVITY_ENDPOINT;
  const send =
    options.send ??
    defaultSend(
      endpoint,
      typeof window !== 'undefined' && typeof window.fetch === 'function'
        ? window.fetch.bind(window)
        : null
    );

  let enabled = false;
  let pending: ActivityEventInput[] = [];
  let timer: ReturnType<typeof setTimeout> | null = null;

  const clearTimer = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const flush = (reason: FlushReason = 'manual') => {
    clearTimer();
    if (pending.length === 0) return;
    const batch = pending;
    pending = [];
    try {
      const result = send(JSON.stringify({ events: batch }), { beacon: reason === 'pagehide' });
      if (result && typeof result.catch === 'function') {
        // Dropped on failure on purpose: an offline retry loop would pile up
        // memory and re-send stale events once the network is back.
        result.catch(() => undefined);
      }
    } catch {
      // Same policy for a synchronous failure.
    }
  };

  return {
    enqueue(event) {
      if (!enabled) return;
      pending.push({ ...event, session_id: event.session_id ?? getOrCreateSessionId() });
      if (pending.length >= maxBatch) {
        flush('size');
        return;
      }
      if (timer === null) timer = setTimeout(() => flush('timer'), flushDelayMs);
    },
    flush,
    setEnabled(next) {
      enabled = next;
      if (!next) {
        clearTimer();
        pending = [];
      }
    },
    size: () => pending.length,
    dispose() {
      clearTimer();
      pending = [];
    }
  };
}

// ---------------------------------------------------------------------------
// Fetch observer
// ---------------------------------------------------------------------------

type ObservedWindow = Window & typeof globalThis & { __genplayActivityFetch?: typeof fetch };

function requestMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) return init.method.toUpperCase();
  if (typeof Request !== 'undefined' && input instanceof Request) return input.method.toUpperCase();
  return 'GET';
}

function requestUrl(input: RequestInfo | URL, base: string): URL | null {
  try {
    if (typeof input === 'string') return new URL(input, base);
    if (input instanceof URL) return input;
    if (typeof Request !== 'undefined' && input instanceof Request) return new URL(input.url, base);
    return null;
  } catch {
    return null;
  }
}

/**
 * Wrap `win.fetch` so every same-origin mutating `/api` call reports one event.
 * Idempotent: a second install on the same window is a no-op. Returns an
 * uninstall function that puts the original fetch back.
 */
export function installFetchObserver(
  win: Window & typeof globalThis,
  onEvent: (event: ActivityEventInput) => void
): () => void {
  const target = win as ObservedWindow;
  if (typeof target.fetch !== 'function') return () => undefined;
  if (target.__genplayActivityFetch) return () => undefined;

  const original = target.fetch;
  target.__genplayActivityFetch = original;

  const observed = function observedFetch(
    this: unknown,
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    const call = original.call(target, input, init);

    let url: URL | null = null;
    let method = 'GET';
    try {
      method = requestMethod(input, init);
      url = requestUrl(input, target.location.origin);
      if (!url || !shouldTrackApiCall(method, url, target.location.origin)) return call;
    } catch {
      return call;
    }

    const started = Date.now();
    const trackedUrl = url;
    const report = (status: number) => {
      try {
        const derived = deriveApiAction(method, trackedUrl.pathname);
        const event: ActivityEventInput = {
          action: derived.action,
          api_path: trackedUrl.pathname,
          http_method: method,
          status_code: status,
          success: status >= 200 && status < 400,
          duration_ms: Math.max(0, Date.now() - started)
        };
        if (derived.entityType) event.entity_type = derived.entityType;
        if (derived.entityId) event.entity_id = derived.entityId;
        onEvent(event);
      } catch {
        // Never let logging break the caller.
      }
    };

    return call.then(
      response => {
        report(response.status);
        return response;
      },
      error => {
        report(0);
        throw error;
      }
    );
  };

  target.fetch = observed as typeof fetch;

  return () => {
    if (target.fetch === observed) target.fetch = original;
    delete target.__genplayActivityFetch;
  };
}

// ---------------------------------------------------------------------------
// Shared queue + trackEvent
// ---------------------------------------------------------------------------

let sharedQueue: ActivityQueue | null = null;

export function getActivityQueue(): ActivityQueue {
  if (!sharedQueue) sharedQueue = createActivityQueue();
  return sharedQueue;
}

/** Tracking is off until a session exists; ActivityTracker flips it. */
export function setActivityEnabled(enabled: boolean): void {
  getActivityQueue().setEnabled(enabled);
}

/**
 * Record a UI-only action that never reaches an API route, e.g.
 * `trackEvent('export.modal-opened', { metadata: { conceptId } })`.
 * Safe to call anywhere on the client; a no-op on the server.
 */
export function trackEvent(action: string, options: Partial<ActivityEventInput> = {}): void {
  if (typeof window === 'undefined') return;
  try {
    getActivityQueue().enqueue({ ...options, action });
  } catch {
    // Logging must never surface as an application error.
  }
}

/** Tests only: replace the shared queue. */
export function resetActivityClient(options?: ActivityQueueOptions): void {
  sharedQueue?.dispose();
  sharedQueue = options ? createActivityQueue(options) : null;
  memorySession = null;
}
